import ImageWithCaption from '../../common/components/ImageWithCaption/ImageWithCaption'
import PageLayout from '../../common/components/PageLayout/PageLayout'
import References from '../../common/components/References/References'
import TextRef from '../../common/components/TextRef/TextRef'
import Title from '../../common/components/Title/Title'
import Video from '../../common/components/Video/Video'
import Images from '../../common/Images'
import { columns, data, customRowStyles } from './datatable'
import DataTable from 'react-data-table-component'
import './Model.css'
import NavierStokesEquation from './NavierStokesEquation'
import ReynoldsNumberFormula from './ReynoldsNumberFormula'
import Mosaic from '../results/components/Mosaic'
import CaptionWrapper from '../../common/components/CaptionWrapper/CaptionWrapper'
import ImagesMosaic from '../../common/components/ImagesMosaic/ImagesMosaic'
import MosaicRef from '../../common/components/ImagesMosaic/MosaicRef'
import TableOfContent, {
  Parent
} from '../../common/components/TableOfContents/TableOfContent'

function Model () {
  const imageUrls: string[] = [
    Images.Model_rhag_monomer,
    Images.Model_rhce_monomer,
    Images.Model_rhd_monomer,
    Images.Model_rhag_homodimer,
    Images.Model_rhce_homodimer,
    Images.Model_rhce_rhag_heterodimer,
    Images.Model_rhce_rhd1_heterodimer,
    Images.Model_rhd1_homodimer,
    Images.Model_rhd1_rhag_heterodimer,
    Images.Model_rhag_homotrimer,
    Images.Model_rhagx2_rhce_heterotrimer,
    Images.Model_rhagx2_rhd_heterotrimer,
    Images.Model_rhce_homotrimer,
    Images.Model_rhcex2_rhag_heterotrimer,
    Images.Model_rhcex2_rhd_heterotrimer,
    Images.Model_rhd_homotrimer,
    Images.Model_rhd1_rhag_rhce_heterotrimer,
    Images.Model_rhdx2_rhag_heterotrimer,
    Images.Model_rhdx2_rhce_heterotrimer
  ]

  const toContent: Parent[] = [
    {
      displayName: 'Protein structure prediction',
      id: 'prot-struct-prediction',
      children: [
        {
          displayName: 'Sequence alignment',
          id: 'seq-align'
        },
        {
          displayName: 'Secondary structure prediction',
          id: 'sec-struct-prediction'
        },
        {
          displayName: '3D structure prediction',
          id: '3d-struct-prediction'
        }
      ]
    },
    {
      displayName: 'Molecular dynamics',
      id: 'molecular-dynamics',
      children: []
    },
    {
      displayName: 'Computational Fluid Dynamics (CFD)',
      id: 'computational-fluid-dynamics',
      children: [
        {
          displayName: 'Pressure-velocity coupling',
          id: 'pressure-velocity-coupling'
        },
        {
          displayName: 'Spatial discretization',
          id: 'spatial-discretization'
        },
        {
          displayName: 'Convergence',
          id: 'convergence'
        }
      ]
    },
    {
      displayName: 'References',
      id: 'references-top',
      children: []
    }
  ]

  const scrollInView = (id?: string) => {
    const idToScroll = id || 'mosaic'
    const mosaic = document.getElementById(idToScroll)?.getBoundingClientRect()
    if (mosaic) {
      const y = mosaic.y + window.scrollY - 100
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      })
    }
  }

  return (
    <PageLayout title="Project Modelling">
      <div className="model-top">
        <div className="model-toc-container">
          <div className="model-toc-flex">
            <p>
              Concurrently with our wet lab analyses, we completed multiple
              modelling analyses. While our wet lab work focused mainly on the A
              and B antigens of blood, much of our lab work aimed to understand
              and model the Rh antigen, another very immunogenic component of
              red blood cells. Our approach used a combination of protein
              structural predictions and molecular dynamics to investigate the
              Rh protein complex. Furthermore, we also employed a computational
              fluid dynamics approach to simulate a fluidic delivery system for
              our enzymes and investigate important parameters that we should
              take into consideration when designing such a model.
            </p>
          </div>
          <div className="model-toc">
            <TableOfContent content={toContent} />
          </div>
        </div>
      </div>
      <br />
      <Title title="Protein structure prediction" id="prot-struct-prediction" />
      <p>
        Our literature review revealed the absence of a published tridimensional
        structure, experimental or <i>in silico</i>, of the Rhesus protein
        complex with its most immunogenic subunit, RhD. As any future work on
        our project or one similar would require that structure, we decided to
        build the first 3D model of RhD in its protein complex.
      </p>
      <p>
        With no structure available, we went with a <i>de novo</i> approach with
        homology elements, as there are related proteins in the Rh protein
        family.
      </p>
      <Title title="1. Sequence alignment" isSub id="seq-align" />
      <p>
        {' '}
        We used the BLAST suite tool COBALT <TextRef sup="1" content="" /> to
        perform sequence alignments with the Rh protein family. The proteins
        chosen were from humans (two RhD isoforms, RhD1 and RhD7; RhAG; RhCE;
        RhCG and RhBG) and from amoebas (RhGA and RhGB). RhD isoforms, RhAG and
        RhCE are expressed in erythrocytes. RhCG and RhBG are expressed in
        epithelial kidney cells. RhGA and RhGB are amoeba Rh-like proteins
        involved with the contractile vacuole. We chose these proteins to have
        sequences with various levels of similarity to each other and to have a
        global idea of the relations between members of the Rh protein family.
      </p>
      <p>
        We performed a global alignment between all query sequences and
        one-on-one alignments between the human Rh proteins.
      </p>
      <div className="figures-side-by-side">
        <ImageWithCaption
          figure={1}
          caption="Graphic representation of the alignment between RhD1 and RhD7."
          src={Images.Graph1}
          imagePadding={20}
          captionPadding={0}
        />
        <ImageWithCaption
          figure={2}
          caption="Graphic representation of the alignment between RhD7 and RhCE."
          src={Images.Graph2}
          imagePadding={20}
          captionPadding={0}
        />
      </div>
      <div className="figures-side-by-side figures-side-by-side-bottom">
        <ImageWithCaption
          figure={3}
          caption="Graphic representation of the alignment between RhD1 et RhCE."
          src={Images.Graph3}
          imagePadding={20}
          captionPadding={0}
        />
        <ImageWithCaption
          figure={4}
          caption="Graphic representation of the alignment between RhAG and RhCE."
          src={Images.Graph4}
          imagePadding={20}
          captionPadding={0}
        />
      </div>
      <Title
        title="2. Secondary structure prediction"
        isSub
        id="sec-struct-prediction"
      />
      <p>
        {' '}
        After comparing the sequences, we predicted the structural motifs of the
        erythrocyte Rh proteins using PSIPRED <TextRef sup="2" content="" /> and
        the disordered regions using DISOPRED <TextRef sup="3" content="" />.
      </p>
      <p>
        {' '}
        The transmembrane segments were predicted with the DeepTMHMM
        <TextRef sup="4" content="" /> tool. All helixes are transmembrane, with
        cytosolic N and C-terminal ends.
      </p>
      <Title
        title="3. 3D structure prediction"
        isSub
        id="3d-struct-prediction"
      />
      <p>
        We used ColabFold <TextRef sup="5" content="" /> to test all possible
        monomers, dimers and trimers of the 3 Rh subunits (RhD, RhCE, RhAG) with
        AlphaFold2 Multimer <TextRef sup="6" content="" />. We used the standard
        parameters, with use of dropout in the deep learning steps.
      </p>
      <ImagesMosaic id="mosaic" imageUrls={imageUrls} />
      <p>
        After creating all 20 protein models, we evaluated their binding energy
        with FoldX <TextRef sup="7" content="" /> to reflect biological reality.
        FoldX is a tool that provides a fast and quantitative importance
        estimation of the interactions contributing to the stability of proteins
        and protein complexes. The most stable multimers were these trimers :
        RhAG homotrimer <MosaicRef index={10} onClick={() => scrollInView()} />,
        2xRhAG with RhCE <MosaicRef index={11} onClick={() => scrollInView()} />
        , RhD homotrimer <MosaicRef
          index={17}
          onClick={() => scrollInView()}
        />{' '}
        and 2xRhAG with RhD{' '}
        <MosaicRef index={18} onClick={() => scrollInView()} />.
      </p>
      <br />
      <Title title="Molecular dynamics" id="molecular-dynamics" />
      <p>
        The sequence alignment, as well as the secondary and tertiary
        predictions, indicated that RhCE and RhD are extremely similar. To gain
        a better understanding of their structure and their interactions in
        biological conditions, we performed molecular dynamics analyses.{' '}
      </p>
      <p>
        A bilayer membrane and protein system for each of the two stable
        heterotrimers with RhCE and RhD was constructed with OPM PPM3
        <TextRef sup="8" content="" /> and Charmm-Gui
        <TextRef sup="9" content="" />.
      </p>
      <div className="figures-side-by-side special-height">
        <ImageWithCaption
          figure={5}
          caption="2xRhAG-RhCE complex in plasma membrane."
          src={Images.Model_rhce_ppm}
          imagePadding={20}
          captionPadding={0}
        />
        <ImageWithCaption
          figure={6}
          caption="2xRhAG-RhD complex in plasma membrane."
          src={Images.Model_rhd_ppm}
          imagePadding={20}
          captionPadding={0}
        />
      </div>
      <div className="figures-side-by-side figures-side-by-side-bottom">
        <ImageWithCaption
          figure={7}
          caption="2xRhAG-RhCE complex in water and ion box."
          src={Images.Model_rhce_box}
          imagePadding={20}
          captionPadding={0}
        />
        <ImageWithCaption
          figure={8}
          caption="2xRhAG-RhD complex in water and ion box."
          src={Images.Model_rhd_box}
          imagePadding={20}
          captionPadding={0}
        />
      </div>
      <p>
        Using the NAMD molecular dynamics program
        <TextRef sup="10" content="" />, we ran molecular dynamics simulations
        of 200 ns for both systems. System parameters were:
      </p>
      <ul>
        <li>
          200 lipids for the outer membrane leaflets, and 154 lipids for the
          inner membrane leaflets. The mammalian plasma membrane lipid
          composition was previously reported in Pogozheva et al., 2022
          <TextRef sup="11" content="" />.
        </li>
        <li>
          A water layer of 30 Å on top and bottom of the membrane and Rh trimer
          system.
        </li>
        <li>A simulation temperature of 303.15 K.</li>
        <li>Concentration of 0.15 M of NaCL ions and 0.15 M of KCl ions.</li>
        <li>The other default parameters were used.</li>
      </ul>
      <Video
        videos={[
          {
            title: 'ULaval: RhCE_front (2023) [English]',
            url: 'https://video.igem.org/videos/embed/cfa7b202-9f0d-42f3-a0f2-7e9875640dd1'
          },
          {
            title: 'ULaval: RhCE_top (2023) [English]',
            url: 'https://video.igem.org/videos/embed/93b18eb8-21ea-4ff9-a13c-9907398c3622'
          }
        ]}
        figureLegend={<>Front and top views of the RhCE complex.</>}
      />
      <Video
        videos={[
          {
            title: 'ULaval: RhD_front (2023) [English]',
            url: 'https://video.igem.org/videos/embed/0d95f790-4654-462a-8749-e517468442ff'
          },
          {
            title: 'ULaval: RhD_top (2023) [English]',
            url: 'https://video.igem.org/videos/embed/35114ff0-1ffc-41ce-bb2e-0f8405c5eb47'
          }
        ]}
        figureLegend={<>Front and top views of the RhD complex.</>}
      />
      <br />
      <Title
        title="Computational Fluid Dynamics (CFD)"
        id="computational-fluid-dynamics"
      />
      <p>
        Next, we sought to design an efficient system through which blood could
        betreated directly. We analyzed different systems, such as gravity-based
        vertical filter cylinders and dialysis-like systems. We finally settled
        on a horizontal cylinder filled with baffles and with our treatment
        enzymes affixed to the inner wall of the cylinder through which blood
        would flow. This system offers many advantages, most notable of which is
        the ability to maximize blood homogeneity through the system, thus
        allowing us to be certain that every blood cell that goes through it
        comes into contact with the enzymes and is treated. We used
        computational fluid dynamics (CFD) analyses to simulate and evaluate
        multiple versions of this system with varying parameters of diameters,
        baffle length, distance between baffles, and initial pressure. These
        simulations aimed to identify parameters that influence the homogeneity
        of the blood flow within the system and thus its efficiency. This would
        allow us to take into account these important parameters when designing
        the system.
      </p>
      <p>
        The homogeneity of a fluid flow is difficult to assess. Turbulence, a
        phenomenon that can occur in a fluid when under the right circumstances,
        leads to mixing of a solution and thus to better solution homogeneity.
        The behavior of flows of viscous fluids can be predicted using the{' '}
        <b>Navier-Stokes equation for incompressible flow</b>
        <TextRef sup="12" content="" />.{' '}
      </p>
      <NavierStokesEquation />{' '}
      {/* latex :  \frac{\partial u}{\partial t} + (u \cdot \nabla)u - v\nabla^2u = - \frac{1}{\rho}\nabla p + g */}
      <p>
        A simplified derivative of this equation, the <b>Reynolds number</b>{' '}
        formula can also be used in simple cases to predict whether a flow will
        be laminar or turbulent.{' '}
      </p>
      <ReynoldsNumberFormula />{' '}
      {/* latex :  Re = \frac{uL}{v} = \frac{\rho uL}{\mu} */}
      <p>
        In this equation, <i>Re</i> is the reynolds number, <i>u</i> is the flow
        rate in meters per seconds, <i>L</i> is the characteristic length of the
        medium in meters, <i>𝒗</i> is the kinematic viscosity of the fluid in
        meters squared per second and <i>μ</i> is the dynamic viscosity of the
        fluid in kilograms per meters per second. As the <i>Re</i> increases so
        does the chances of the flow to be turbulent rather than laminar{' '}
        <TextRef sup="13" content="" />. As presented by the equation, a higher
        velocity has higher chances of leading to a more turbulent flow.
        Velocity can therefore be used as a proxy for turbulence and
        homogeneity.
      </p>
      <p>
        We tested for various values of parameters that can influence the
        characteristic length of the system: channel diameter, baffles width,
        and the distance between baffles. We also tested various settings of
        pressure, a variable that can affect the velocity (flow rate) of the
        fluid.
      </p>
      <p>
        Since testing all the different combinations would require extensive
        modelling, we proceeded with a full-factorial analysis in order to
        simplify the analysis and to estimate the best working conditions. In
        that regard, we applied a full-factorial central composite design using
        4 factors/independent variables (respectively the channel diameter (
        <i>
          X<sub>1</sub>
        </i>
        ), the distance between baffles (
        <i>
          X<sub>2</sub>
        </i>
        ), the baffles&apos; width (
        <i>
          <sub>X3</sub>
        </i>
        ) and the inlet pressure (
        <i>
          X<sub>4</sub>
        </i>
        )) with 3 levels each in one replicate organized in one block.{' '}
        <i>
          X<sub>1</sub>
        </i>{' '}
        levels are 0.5, 1.0, 2.0 mm,{' '}
        <i>
          X<sub>2</sub>
        </i>{' '}
        levels are 2.5, 5.0, 10.0 mm,{' '}
        <i>
          X<sub>3</sub>
        </i>{' '}
        levels are 1.0, 2.0 and 3.0 mm and{' '}
        <i>
          X<sub>4</sub>
        </i>{' '}
        levels are 10.0, 100.0, 1000 psi. This generated a total of 81 runs. The
        test or dependent variable was the magnitude velocity (Y) of blood in
        the system. In order to speed up the computation, we optimized the
        design to a set of nine runs{' '}
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => scrollInView('table1')}
        >
          (Table 1)
        </span>
        . Only the independent variables{' '}
        <i>
          X<sub>1</sub>
        </i>
        ,{' '}
        <i>
          X<sub>2</sub>
        </i>
        ,{' '}
        <i>
          X<sub>3</sub>
        </i>{' '}
        and{' '}
        <i>
          X<sub>4</sub>
        </i>{' '}
        through an order of 1 were included. The number of runs was reduced by
        sequential optimization with a search procedure using an exchange method
        with number of exchanges points of 1. This generated the runs in bold,
        namely runs 3, 16, 23, 35, 42, 46, 58, 65 and 81. Corresponding geometry
        and meshes were thus generated for the CFD simulation.
      </p>
      <div className="datatable-wrapper" id="table1">
        <DataTable
          columns={columns}
          data={data}
          title="Table 1. Full-factorial design matrix of level values of factors for fluidic system optimisation"
          responsive
          conditionalRowStyles={customRowStyles}
        />
      </div>
      <p>
        Blood is an inverse non-newtonian fluid and as such its viscosity
        decreases with increasing pressure. We thus had to use a complex method
        to simulate it accurately. Blood was approximated in the simulations by
        using a density of 1060 kg/m3 and a carreau
        <TextRef sup="14" content="" /> fluid model for viscosity (s = 3.313, n
        = 0.3568, zero shear viscosity = 0.056 kg/m*s, and infinite shear
        viscosity = 0.0035 kg/m*s) <TextRef sup="15" content="" />. The
        simulations were solved as laminar flow due to the software limitations
        of not being able to compute the flow of carreau model fluids in
        non-laminar flows, and thus of not being able to accurately replicate
        blood in simulations with turbulence. There are nonetheless some
        approximations of blood that can be used to incorporate more realistic
        turbulence in such a simulation. We opted to use the carreau model here
        to evaluate the impact of parameters on flow homogeneity with the most
        faithful simulation possible, as blood only very rarely forms turbulent
        flows due to its high viscosity. The parameters used to model blood
        combined with the laminar flow simulation and knowledge of how turbulent
        (and homogeneous) a fluid becomes as velocity increases (according to
        the “Reynolds number for flow in a pipe” formula) nonetheless gave us an
        accurate overall model for the purposes of this experiment.
      </p>
      <p>
        All CFD analyses were completed using ANSYS Workbench 2023 R2
        <TextRef sup="16" content="" /> software. The models were created using
        a combination of 2D geometrical shapes and computational meshes made to
        fit the inside of the geometry. 2D models, while only slightly less
        accurate than 3D ones, need a much lighter computational workload and
        allow us to have results much faster. This in turn allowed us to
        simulate and test more models to investigate more varied parameters. The
        geometrical shapes and meshes were created using ANSYS DesignModeler.
        Blood flow through these 2D simulated channels was then computed using
        ANSYS Fluent, a tool specialized in fluid flow simulations. We used a
        pressure-based solver.
      </p>
      <br />
      <Title
        title="Pressure-velocity coupling"
        isSub
        id="pressure-velocity-coupling"
      />
      <p>
        The pressure-velocity coupling in the simulation was solved using the
        SIMPLE scheme <TextRef sup="17" content="" />{' '}
        <TextRef sup="18" content="" />, a computationally efficient but
        remarkably accurate algorithm that is recommended by the manufacturer
        for most situations. This algorithm uses velocity and pressure
        corrections to accurately estimate the pressure field while still
        enforcing mass conservation. The algorithm SIMPLEC would have been a
        close alternative as it is only dissimilar to the SIMPLE algorithm in
        that it remains consistent when using multi-dimensional coupling. Since
        we only took into consideration pressure-velocity coupling in this case,
        the SIMPLE algorithm allowed us to optimize our computational time while
        still retaining accuracy. Other more complicated schemes such as COUPLED
        or PISO would also only have offered a significant advantage if our
        simulation included coupled variables, such as temperature-dependent
        pressure or similar.
      </p>
      <br />
      <Title title="Spatial discretization" isSub id="spatial-discretization" />
      <p>
        ANSYS Fluent requires the space to be discretized for the computations
        to be possible. This means that we had to choose an algorithm to
        transform the continuous simulated meshed space within the channel
        geometry into a set of finite computational regions in accordance with
        the mesh&apos;s specification. The chosen algorithm can have a
        significant impact on the accuracy of the simulation and so must be
        chosen with great care. After comparing available algorithms, we opted
        for a least square cell-based pressure gradient
        <TextRef sup="19" content="" /> with second order pressure and second
        order upwind momentum. These equations are the most efficient for the
        type of model we wished to compute as they optimize computational burden
        while still retaining second-order accuracy. Gravity was included in the
        simulation according to a 9.8 m/s<sup>2</sup> acceleration and time was
        kept steady.
      </p>
      <br />
      <Title title="Convergence" isSub id="convergence" />
      <p>
        All models were run for 1000 iterations or until residuals for all
        x-velocity, y-velocity, and continuity converged to 0.0001. This
        convergence threshold was enough to guarantee us a high level of
        confidence in the results considering the low amount of parameters.{' '}
      </p>
      <p>
        The accuracy of the blood flow was verified by inspecting that the
        molecular viscosity of the fluid varied correctly in function of the
        pressure. Simulation fidelity was also verified by inspecting the
        pressure gradient across the fluid through the channel to confirm its
        degradation over distance.
      </p>
      <CaptionWrapper
        contentPadding={'0 8px 8px 8px'}
        captionPadding={0}
        caption="Velocity as a function of distance plots and diagrams for the 9 simulated models. See Table 1 for models parameters."
      >
        <Mosaic
          combos={[
            {
              graph: Images.CDF1Graph1,
              pipe: Images.CDF1Pipe1
            },
            {
              graph: Images.CDF1Graph2,
              pipe: Images.CDF1Pipe2
            },
            {
              graph: Images.CDF1Graph3,
              pipe: Images.CDF1Pipe3
            },
            {
              graph: Images.CDF1Graph4,
              pipe: Images.CDF1Pipe4
            },
            {
              graph: Images.CDF1Graph5,
              pipe: Images.CDF1Pipe5
            },
            {
              graph: Images.CDF1Graph6,
              pipe: Images.CDF1Pipe6
            },
            {
              graph: Images.CDF1Graph7,
              pipe: Images.CDF1Pipe7
            },
            {
              graph: Images.CDF1Graph8,
              pipe: Images.CDF1Pipe8
            },
            {
              graph: Images.CDF1Graph9,
              pipe: Images.CDF1Pipe9
            }
          ]}
        />
      </CaptionWrapper>
      <p>
        Initial analyses revealed that all simulations started with high levels
        of viscosity. This is explainable by the nature of these simulations
        that confer great inertia to the fluid at the entrance to the pipe.
        Viscosity nonetheless stabilizes quickly as the force from the pressure
        gets transferred to the fluid. The various settings of pressure we
        investigated led to great differences in velocity profiles through the
        simulated channels. This in turn made it difficult to assess the
        differences in predicted homogeneity through velocity between different
        models. Thus, we decided to re-compute the models while keeping pressure
        constant at 10 psi. This aimed at investigating the influence of the
        channel geometry on flow homogeneity.
      </p>
      <CaptionWrapper
        contentPadding={'0 8px 8px 8px'}
        captionPadding={0}
        caption="Velocity as a function of distance plots and diagrams for the 9 simulated models wit constant p = 10 psi."
      >
        <Mosaic
          combos={[
            {
              graph: Images.CDF2Graph1,
              pipe: Images.CDF2Pipe1
            },
            {
              graph: Images.CDF2Graph2,
              pipe: Images.CDF2Pipe2
            },
            {
              graph: Images.CDF2Graph3,
              pipe: Images.CDF2Pipe3
            },
            {
              graph: Images.CDF2Graph4,
              pipe: Images.CDF2Pipe4
            },
            {
              graph: Images.CDF2Graph5,
              pipe: Images.CDF2Pipe5
            },
            {
              graph: Images.CDF2Graph6,
              pipe: Images.CDF2Pipe6
            },
            {
              graph: Images.CDF2Graph7,
              pipe: Images.CDF2Pipe7
            },
            {
              graph: Images.CDF2Graph8,
              pipe: Images.CDF2Pipe8
            },
            {
              graph: Images.CDF2Graph9,
              pipe: Images.CDF2Pipe9
            }
          ]}
        />
      </CaptionWrapper>
      <p>
        Velocity profiles were much more similar across these models. This
        allowed us to evaluate their performance based on a visual assessment of
        the homogeneity of flow velocity through the channel and of how the flow
        behaved in dead zones created after the flow crosses a baffle. This
        initial evaluation allowed us to conclude that all parameters
        investigated had a significant effect on flow homogeneity. Geometry
        changes created more or less dead zones after the baffles, leading to
        parts of the mixture with very low homogeneity. The fourth model was
        evaluated as being the best according to the criteria listed above. This
        model had better continuous velocity and very few dead zones of low flow
        rate.
      </p>
      <div className="figures-side-by-side centered-figures-side-by-side">
        <CaptionWrapper
          figure={9}
          contentPadding={'20px'}
          captionPadding={0}
          caption="Graph and pipe for more specific analysis of the 4th model."
        >
          <span className="stacked-images">
            <img src={Images.Model_cfd3_graph} />
            <img src={Images.Model_cfd3_pipe} />
          </span>
        </CaptionWrapper>
        <ImageWithCaption
          figure={10}
          caption="Pressure for more specific analysis of the 4th model."
          src={Images.Model_cfd3_pressure}
          imagePadding={20}
          captionPadding={0}
        />
      </div>
      <p>
        This model could still however be improved before moving on to possible
        next stages of development. The obvious next step in this analysis is to
        use 3D structural geometry modelling to generate new 3D models that take
        into account what we have learned from these exploratory analyses.
        Additional modifications to the geometry could also be incorporated. The
        addition of a smaller injector channel at the inlet connecting with the
        main channel instead of the initial zone with no baffles, as an example,
        could greatly increase overall homogeneity and thus the system’s
        efficiency. potentially enhance the mixture homogeneity and thus
        efficiency.{' '}
      </p>
      <p>
        Models could also include a negative pressure created at the channel
        outlet to further enhance the continuous flow of blood through the
        channel. This constant pressure would help the flow velocity be constant
        and could potentially further increase the mixture homogeneity.
      </p>
      <p>
        Finally, the use of a different model that uses a rougher approximation
        for blood but that allows the inclusion of turbulence dynamics could
        give us some insight on the behavior of the blood flow in high velocity
        zones.{' '}
      </p>
      <References
        references={[
          <span key={1}>
            Papadopoulos JS, Agarwala R. COBALT: constraint-based alignment tool
            for multiple protein sequences. Bioinformatics. 2007 May
            1;23(9):1073-9. doi: 10.1093/bioinformatics/btm076. Epub 2007 Mar 1.
            PMID: 17332019.
          </span>,
          <span key={2}>
            Buchan DWA, Jones DT (2019). The PSIPRED Protein Analysis Workbench:
            20 years on. Nucleic Acids Research.
            https://doi.org/10.1093/nar/gkz297
          </span>,
          <span key={3}>
            Jones DT, Cozzetto D. (2015). DISOPRED3: precise disordered region
            predictions with annotated protein-binding activity. Bioinformatics.
            2015 Mar 15;31(6):857-63. doi: 10.1093/bioinformatics/btu744. Epub
            2014 Nov 12.
          </span>,
          <span key={4}>
            Jeppe Hallgren, Konstantinos D. Tsirigos, Mads D. Pedersen, José
            Juan Almagro Armenteros, Paolo Marcatili, Henrik Nielsen, Anders
            Krogh and Ole Winther (2022). DeepTMHMM predicts alpha and beta
            transmembrane proteins using deep neural networks.
            https://doi.org/10.1101/2022.04.08.487609
          </span>,
          <span key={5}>
            Mirdita, M., Schütze, K., Moriwaki, Y. et al. ColabFold: making
            protein folding accessible to all. Nat Methods 19, 679–682 (2022).
            https://doi.org/10.1038/s41592-022-01488-1
          </span>,
          <span key={6}>
            Evans, R. et al. (2022). Protein complex prediction with
            AlphaFold-Multimer (p. 2021.10.04.463034). bioRxiv.
            https://doi.org/10.1101/2021.10.04.463034
          </span>,
          <span key={7}>
            Schymkowitz J, Borg J, Stricher F, Nys R, Rousseau F, Serrano L. The
            FoldX web server: an online force field. Nucleic Acids Res. 2005 Jul
            1;33(Web Server issue):W382-8. doi: 10.1093/nar/gki387. PMID:
            15980494; PMCID: PMC1160148.
          </span>,
          <span key={8}>
            Lomize AL, Todd SC, Pogozheva ID. Spatial arrangement of proteins in
            planar and curved membranes by PPM 3.0. Protein Sci. 2022
            Jan;31(1):209-220. doi: 10.1002/pro.4219. Epub 2021 Nov 8. PMID:
            34716622; PMCID: PMC8740824.
          </span>,
          <span key={9}>
            Jo S, Kim T, Iyer VG, Im W. CHARMM-GUI: a web-based graphical user
            interface for CHARMM. J Comput Chem. 2008;29(11):1859-1865.
            doi:10.1002/jcc.20945. PMID: 18351591.
          </span>,
          <span key={10}>
            Phillips JC, Hardy DJ, Maia JDC, et al. Scalable molecular dynamics
            on CPU and GPU architectures with NAMD. J Chem Phys.
            2020;153(4):044130. doi:10.1063/5.0014475. PMID: 32752662.
          </span>,
          <span key={11}>
            I.D. Pogozheva, G.A. Armstrong, L. Kong, T.J. Hartnagel, C.A.
            Carpino, S.E. Gee, D.M. Picarello, A.S. Rubin, J. Lee, S. Park, A.L.
            Lomize, and W. Im (2022). Comparative Molecular Dynamics Simulation
            Studies of Realistic Eukaryotic, Prokaryotic, and Archaeal
            Membranes. J. Chem. Inf. Model. 62:1036-1051
          </span>,
          <span key={12}>
            Batchelor, G. K. (2010). An Introduction to fluid dynamics (14.
            print). Cambridge Univ. Press.
          </span>,
          <span key={13}>
            Boundary Layer. (s. d.). Glenn Research Center | NASA.
            https://www1.grc.nasa.gov/beginners-guide-to-aeronautics/boundary-layer/
          </span>,
          <span key={14}>
            Akbar, N. S., & Nadeem, S. (2014). Carreau fluid model for blood
            flow through a tapered artery with a stenosis. Ain Shams Engineering
            Journal, 5(4), 1307-1316. https://doi.org/10.1016/j.asej.2014.05.010
          </span>,
          <span key={15}>
            Solution Setup of Ansys Fluent Model for Flow Field | Ansys Courses.
            (s. d.). ANSYS Innovation Courses.
            https://courses.ansys.com/index.php/courses/fluent-3d-bifurcating-artery/lessons/physics-setup-lesson-5/topic/solution-setup/
          </span>,
          <span key={16}>Ansys® Workbench 2023, Release 2</span>,
          <span key={17}>
            Latimer, B. R., & Pollard, A. (1985). Comparison of
            Pressure-Velocity Coupling Solution Algorithms. Numerical Heat
            Transfer, 8(6), 635-652. https://doi.org/10.1080/01495728508961876
          </span>,
          <span key={18}>
            Zogheib, B., & Barron, R. M. (2011). Velocity-pressure coupling in
            finite difference formulations for the Navier-Stokes equations.
            International Journal for Numerical Methods in Fluids, 65(9),
            1096-1114. https://doi.org/10.1002/fld.2231
          </span>,
          <span key={19}>
            Min, T., Yoo, J. Y., & Choi, H. (2001). Effect of spatial
            discretization schemes on numerical solutions of viscoelastic fluid
            flows. Journal of Non-Newtonian Fluid Mechanics, 100(1), 27-47.
            https://doi.org/10.1016/S0377-0257(01)00128-8
          </span>
        ]}
      />
    </PageLayout>
  )
}

export default Model
